import { useState, useEffect } from 'react';
import type { TopTab } from '../pages/ChatbotDetail';

export interface SetupCompletion {
  hasInstructions: boolean;
  hasKnowledge: boolean;
  hasDomain: boolean;
  emailConnected: boolean;
  hasTrainingEmails: boolean;
}

interface SetupStep {
  label: string;
  complete: boolean;
  tab: TopTab;
}

interface SetupProgressProps {
  chatbotId: string;
  completion: SetupCompletion;
  emailTabVisible: boolean;
  onNavigateTab: (tab: TopTab) => void;
}

const TAB_LABELS: Partial<Record<TopTab, string>> = {
  'chatbot': 'Chatbot',
  'knowledge-base': 'Knowledge Base',
  'email-responder': 'Email',
};

export default function SetupProgress({
  chatbotId,
  completion,
  emailTabVisible,
  onNavigateTab,
}: SetupProgressProps) {
  const storageKey = `ni-setup-dismissed-${chatbotId}`;

  const steps: SetupStep[] = [
    { label: 'Describe your business', complete: completion.hasInstructions, tab: 'knowledge-base' },
    { label: 'Upload a document or add an FAQ', complete: completion.hasKnowledge, tab: 'knowledge-base' },
    { label: 'Add your website domain', complete: completion.hasDomain, tab: 'chatbot' },
    ...(emailTabVisible ? [
      { label: 'Connect your email account', complete: completion.emailConnected, tab: 'email-responder' as TopTab },
      { label: 'Add an example email reply', complete: completion.hasTrainingEmails, tab: 'email-responder' as TopTab },
    ] : []),
  ];

  const completedCount = steps.filter(s => s.complete).length;
  const allComplete = completedCount === steps.length;
  const percent = steps.length > 0 ? Math.round((completedCount / steps.length) * 100) : 0;

  const [dismissed, setDismissed] = useState(() =>
    localStorage.getItem(storageKey) === 'true'
  );
  const [wasComplete, setWasComplete] = useState(allComplete);

  // Re-show if steps regress after being fully complete
  useEffect(() => {
    if (allComplete) {
      setWasComplete(true);
    } else if (wasComplete && dismissed) {
      setDismissed(false);
      localStorage.removeItem(storageKey);
      setWasComplete(false);
    }
  }, [allComplete, wasComplete, dismissed, storageKey]);

  if (dismissed) return null;

  return (
    <div className="setup-progress">
      <div className="setup-progress-header">
        <h4>
          {allComplete
            ? 'Setup Complete'
            : `Setup Progress: ${completedCount} of ${steps.length} steps`
          }
        </h4>
        <button
          className="setup-progress-dismiss"
          onClick={() => {
            setDismissed(true);
            localStorage.setItem(storageKey, 'true');
          }}
          title="Dismiss"
        >
          &times;
        </button>
      </div>

      <div className="setup-progress-bar-track">
        <div className="setup-progress-bar-fill" style={{ width: `${percent}%` }} />
      </div>

      {allComplete ? (
        <div className="setup-complete-banner">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
            <polyline points="22,4 12,14.01 9,11.01" />
          </svg>
          <span>All set! Your AI assistant is fully configured.</span>
        </div>
      ) : (
        <div className="setup-progress-steps">
          {steps.map((step, i) => (
            <div key={i} className={`setup-step ${step.complete ? 'complete' : 'incomplete'}`}>
              <svg className="setup-step-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                {step.complete ? (
                  <>
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22,4 12,14.01 9,11.01" />
                  </>
                ) : (
                  <circle cx="12" cy="12" r="10" />
                )}
              </svg>
              <span className="setup-step-label">{step.label}</span>
              {!step.complete && (
                <button className="setup-step-link" onClick={() => onNavigateTab(step.tab)}>
                  Go to {TAB_LABELS[step.tab] || step.tab}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
